import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Table,
    FormGroup,
    Label,
    Spinner,
    Alert,
    Card,
    CardBody,
    Row,
    Col,
    Badge,
    InputGroup,
    InputGroupText
} from 'reactstrap';
import axios from 'axios';

interface OrderLine {
    id: string;
    rowid: string;
    fk_product: string;
    product_ref: string;
    product_label: string;
    description: string;
    qty: string;
    subprice: string;
    total_ht: string;
    total_ttc: string;
    tva_tx: string;
    remise_percent: string;
    rang: string;
    product_barcode?: string;
}

interface ClientInfo {
    clientid: string;
    nom?: string;
    name_alias?: string;
    email?: string;
    phone?: string;
    address?: string;
    zip?: string;
    town?: string;
}

interface OrderData {
    data: {
        id: string;
        ref: string;
        date: number;
        date_commande: number;
        date_validation: number;
        fk_soc: string;
        total_ht: string;
        total_ttc: string;
        status: string;
        cond_reglement: string;
        lines: OrderLine[];
        last_main_doc: string;
        thirdparty?: {
            nom: string;
            name_alias: string;
            email: string;
            phone: string;
            address: string;
            zip: string;
            town: string;
        };
    };
    client?: ClientInfo;
    message: string;
    success: boolean;
}

interface ResponseLine {
    line_id: string;
    qt: number;
    reason: string;
    product_ref: string;
    product_label: string;
    error?: string | null;
}

interface ValidationResponse {
    data: {
        [key: string]: {
            error: string;
            line: ResponseLine;
        };
    };
    entity: number;
    entityname: string;
    message: string;
    post: {
        action: string;
        entity: number;
        lines: ResponseLine[];
        order_id: string;
        order_ref: string;
    };
    success: boolean;
    resultPDF?: string;
}

interface CommandeListItem {
    id: number;
    ref: string;
    type: string;
    label: string;
    date?: string;
    client_name?: string;
}

interface SearchResponse {
    success: boolean;
    message: string;
    data: CommandeListItem[];
    term: string;
    entity: number;
    entityname: string;
}

interface RetourCommandeModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: any;
    orderData?: any;
}

const RetourCommandeModal: React.FC<RetourCommandeModalProps> = ({ isOpen, toggle, user, orderData: initialOrderData }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<CommandeListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [orderData, setOrderData] = useState<OrderData | null>(initialOrderData || null);
    const [selectedCommande, setSelectedCommande] = useState<CommandeListItem | null>(null);
    const [quantites, setQuantites] = useState<{ [key: string]: number }>({});
    const [descriptions, setDescriptions] = useState<{ [key: string]: string }>({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationResponse, setValidationResponse] = useState<ValidationResponse | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const searchTimeoutRef = useRef<number | null>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (initialOrderData) {
            setOrderData(initialOrderData);
            setSelectedCommande({
                id: parseInt(initialOrderData.data?.id || '0'),
                ref: initialOrderData.data?.ref || '',
                type: 'commande',
                label: `Commande ${initialOrderData.data?.ref || ''}`
            });
            setSearchTerm(initialOrderData.data?.ref || '');

            const initialQuantites: { [key: string]: number } = {};
            const initialDescriptions: { [key: string]: string } = {};

            if (initialOrderData.data && initialOrderData.data.lines) {
                initialOrderData.data.lines.forEach((line: OrderLine) => {
                    initialQuantites[line.rowid] = 0;
                    initialDescriptions[line.rowid] = '';
                });
            }

            setQuantites(initialQuantites);
            setDescriptions(initialDescriptions);
            setPdfUrl('');
        }
    }, [initialOrderData]);

    const getStatusBadge = (statusCode: string) => {
        switch (statusCode) {
            case "-1": return { label: "Annulée", color: "danger" };
            case "0": return { label: "Brouillon", color: "warning" };
            case "1": return { label: "Validée", color: "success" };
            case "2": return { label: "En cours", color: "info" };
            case "3": return { label: "Livrée", color: "success" };
            case "4": return { label: "Processed", color: "primary" };
            default: return { label: "Unknown", color: "secondary" };
        }
    };

    const handleSearchList = async (search: string) => {
        if (search.length < 3) {
            setSearchResults([]);
            setDropdownOpen(false);
            return;
        }

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            window.clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debouncing
        searchTimeoutRef.current = window.setTimeout(async () => {
            setSearchLoading(true);
            setDropdownOpen(true);

            const data = {
                action: 'searchref',
                entity: user.entity,
                type: "commande",
                term: search.trim()
            };

            try {
                const response: any = await axios.post<SearchResponse>(
                    'https://api.apapiecesauto.fr/apiV2/',
                    data,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`,
                        }
                    }
                );

                if (response.success) {
                    setSearchResults(response.data);
                    setError('');
                } else {
                    setSearchResults([]);
                    setError(response.message || 'Aucune commande trouvée');
                }
            } catch (error) {
                console.error('Error searching commandes:', error);
                setSearchResults([]);
                setError('Erreur lors de la recherche des commandes');
            } finally {
                setSearchLoading(false);
            }
        }, 300); // 300ms debounce delay
    };

    const handleCommandeSelect = async (commande: CommandeListItem) => {
        setSelectedCommande(commande);
        setSearchTerm(commande.ref);
        setSearchResults([]);
        setDropdownOpen(false);
        setError('');
        await handleSearch(commande.ref);
    };

    const handleSearch = async (commandeRef: string) => {
        if (!commandeRef.trim()) {
            setError('Veuillez sélectionner une commande');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setOrderData(null);
        setQuantites({});
        setDescriptions({});
        setPdfUrl('');
        setShowPdfModal(false);

        try {
            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'orderdetails',
                    entity: user.entity,
                    ref: commandeRef.trim()
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success && response.data) {
                setOrderData(response);

                const initialQuantites: { [key: string]: number } = {};
                const initialDescriptions: { [key: string]: string } = {};
                response.data.lines.forEach((line: OrderLine) => {
                    initialQuantites[line.rowid] = 0;
                    initialDescriptions[line.rowid] = '';
                });
                setQuantites(initialQuantites);
                setDescriptions(initialDescriptions);

            } else {
                setError(response.message || 'Aucun bon de commande trouvé');
            }
        } catch (error) {
            console.error('Error searching order:', error);
            setError('Erreur lors de la recherche du bon de commande');
        } finally {
            setLoading(false);
        }
    };


    const handleQuantiteChange = (lineId: string, value: string) => {
        if (value === '') {
            setQuantites(prev => ({
                ...prev,
                [lineId]: 0
            }));
            return;
        }

        const numValue = parseInt(value);
        if (isNaN(numValue)) return;

        const line = orderData?.data.lines.find(l => l.rowid === lineId);

        if (line) {
            const maxQty = parseInt(line.qty);
            if (numValue >= 0 && numValue <= maxQty) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: numValue
                }));
            } else if (numValue > maxQty) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: maxQty
                }));
            } else if (numValue < 0) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: 0
                }));
            }
        }
    };

    const handleInputFocus = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (quantites[lineId] === 0) {
            e.target.value = '';
        }
    };

    const handleInputBlur = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
            setQuantites(prev => ({
                ...prev,
                [lineId]: 0
            }));
        }
    };

    const handleDescriptionChange = (lineId: string, value: string) => {
        setDescriptions(prev => ({
            ...prev,
            [lineId]: value
        }));
    };

    const handleValidate = async () => {
        if (!orderData) {
            setError('Aucun bon de commande sélectionné');
            return;
        }

        // Check if client exists
    
        const hasQuantities = orderData.data.lines.some(line => {
            const qty = quantites[line.rowid] || 0;
            return qty > 0;
        });

        if (!hasQuantities) {
            setError('Veuillez spécifier au moins une quantité à retourner');
            return;
        }

        setValidating(true);
        setError('');
        setSuccess('');

        try {
            const itemsToReturn = orderData.data.lines
                .filter(line => (quantites[line.rowid] || 0) > 0)
                .map(line => ({
                    line_id: line.rowid,
                    qt: quantites[line.rowid],
                    reason: descriptions[line.rowid] || 'Retour sans motif spécifié',
                    product_ref: line.product_ref,
                    product_label: line.product_label
                }));

            if (itemsToReturn.length === 0) {
                setError('Veuillez spécifier au moins une quantité à retourner');
                setValidating(false);
                return;
            }
            const Obj = {
                action: 'retourCommande',
                entity: user.entity,
                order_ref: orderData.data.ref,
                order_id: orderData.data.id,
                lines: itemsToReturn,
            };

            const response: any = await axios.post<{ success: boolean; message: string; resultPDF?: string }>(
                'https://api.apapiecesauto.fr/apiV2/',
                Obj,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success) {
                setValidationResponse(response);
                if (response.resultPDF) {
                    setPdfUrl(response.resultPDF);
                }
                resetModal();
                setShowValidationModal(true);
            } else {
                setError(response.message || 'Erreur lors de la validation du retour');
            }
        } catch (error) {
            console.error('Error validating retour commande:', error);
            setError('Erreur lors de la validation du retour');
        } finally {
            setValidating(false);
        }
    };

    const calculateTotal = () => {
        if (!orderData) return 0;

        return orderData.data.lines.reduce((total, line) => {
            const lineQty = quantites[line.rowid] || 0;
            const unitPrice = parseFloat(line.subprice);
            const discount = parseFloat(line.remise_percent) || 0;
            const discountedPrice = unitPrice * (1 - discount / 100);
            return total + (lineQty * discountedPrice);
        }, 0);
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleDateString('fr-FR');
    };

    const getClientInfo = () => {
        if (!orderData) {
            return {
                nom: 'Client non disponible',
                name_alias: '',
                email: '',
                phone: '',
                address: '',
                zip: '',
                town: ''
            };
        }

        if (orderData.data?.thirdparty) {
            return orderData.data.thirdparty;
        }

        if (orderData.client?.clientid) {
            return {
                nom: orderData.client.nom || `Client ID: ${orderData.client.clientid}`,
                name_alias: orderData.client.name_alias || '',
                email: orderData.client.email || '',
                phone: orderData.client.phone || '',
                address: orderData.client.address || '',
                zip: orderData.client.zip || '',
                town: orderData.client.town || ''
            };
        }

        return {
            nom: 'Client non disponible',
            name_alias: '',
            email: '',
            phone: '',
            address: '',
            zip: '',
            town: ''
        };
    };

    const resetModal = () => {
        setSearchTerm('');
        setSearchResults([]);
        setSelectedCommande(null);
        setOrderData(null);
        setQuantites({});
        setDescriptions({});
        setError('');
        setSuccess('');
        setLoading(false);
        setSearchLoading(false);
        setValidating(false);
        setPdfUrl('');
        setShowPdfModal(false);
        setDropdownOpen(false);
        toggle();
    };

    const closeValidationModal = () => {
        setShowValidationModal(false);
        setValidationResponse(null);
    };

    const getLineStatusIcon = (line: ResponseLine, error?: string) => {
        if (error) {
            return <i className="ri-close-circle-fill text-danger fs-5" title={error}></i>;
        } else {
            return <i className="ri-checkbox-circle-fill text-success fs-5" title="Succès"></i>;
        }
    };

    const getValidationLines = () => {
        if (!validationResponse) return [];

        const lines: Array<{ line: ResponseLine; error?: string }> = [];

        if (validationResponse.data && typeof validationResponse.data === 'object') {
            Object.keys(validationResponse.data).forEach(key => {
                const item = validationResponse.data[key];
                if (item && item.line) {
                    lines.push({
                        line: item.line,
                        error: item.error
                    });
                }
            });
        }

        if (lines.length === 0 && validationResponse.post?.lines) {
            validationResponse.post.lines.forEach(line => {
                lines.push({
                    line: line,
                    error: undefined
                });
            });
        }

        return lines;
    };

    const openPdfModal = () => {
        setShowPdfModal(true);
    };

    const closePdfModal = () => {
        setShowPdfModal(false);
    };

    const clientInfo = orderData ? getClientInfo() : null;
    const validationLines = getValidationLines();

    return (
        <>
            <Modal isOpen={isOpen} toggle={resetModal} size="lg" centered>
                <ModalHeader toggle={resetModal}>
                    <i className="ri-arrow-go-back-line me-2"></i>
                    Retour de Commande Client
                </ModalHeader>

                <ModalBody>
                    {/* Search Section with Dropdown */}
                    <FormGroup>
                        <Label for="searchCommande" className="fw-bold mb-2">
                            Rechercher une Commande
                        </Label>
                        <div className="position-relative" ref={searchRef}>
                            <InputGroup>
                                <InputGroupText className="bg-light">
                                    <i className="ri-search-line"></i>
                                </InputGroupText>
                                <Input
                                    type="text"
                                    id="searchCommande"
                                    placeholder="Entrez au moins 3 caractères..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handleSearchList(e.target.value);
                                    }}
                                    disabled={loading || validating}
                                    className="border-start-0"
                                />
                                {searchLoading && (
                                    <InputGroupText className="bg-white border-start-0">
                                        <Spinner size="sm" />
                                    </InputGroupText>
                                )}
                            </InputGroup>

                            {/* Search Results Dropdown */}
                            {dropdownOpen && searchResults.length > 0 && (
                                <div
                                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                                    style={{
                                        zIndex: 1000,
                                        maxHeight: '250px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    <div className="p-1">
                                        {searchResults.map((commande) => (
                                            <div
                                                key={commande.id}
                                                className="p-2 border-bottom hover-pointer"
                                                onClick={() => handleCommandeSelect(commande)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong className="text-primary">{commande.ref}</strong>
                                                        <div className="small text-muted">{commande.label}</div>
                                                        {commande.client_name && (
                                                            <div className="small text-muted">
                                                                <i className="ri-user-line me-1"></i>
                                                                {commande.client_name}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <i className="ri-arrow-right-s-line text-muted"></i>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {dropdownOpen && searchTerm.length >= 3 && !searchLoading && searchResults.length === 0 && (
                                <div
                                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-3 text-center"
                                    style={{ zIndex: 1000 }}
                                >
                                    <i className="ri-search-line fs-4 text-muted mb-2 d-block"></i>
                                    <div className="text-muted small">Aucune commande trouvée</div>
                                </div>
                            )}
                        </div>
                        {selectedCommande && (
                            <div className="mt-2">
                                <Alert color="info" className="py-2 mb-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>Commande sélectionnée :</strong> {selectedCommande.ref}
                                        </div>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedCommande(null);
                                                setSearchTerm('');
                                                setOrderData(null);
                                            }}
                                            className="p-0"
                                        >
                                            <i className="ri-close-line"></i>
                                        </Button>
                                    </div>
                                </Alert>
                            </div>
                        )}
                    </FormGroup>

                    {/* Alerts */}
                    {error && <Alert color="danger" className="py-2">{error}</Alert>}
                    {success && <Alert color="success" className="py-2">{success}</Alert>}

                    {/* Loading Spinner for Order Details */}
                    {loading && (
                        <div className="text-center my-4">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Chargement des détails de la commande...</div>
                        </div>
                    )}

                    {/* Order Information */}
                    {orderData && !loading && (
                        <Card className="mt-3 border-primary">
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="d-flex align-items-center mb-2">
                                            <Badge color="primary" className="me-2 fs-14">
                                                Commande
                                            </Badge>
                                            <strong className="fs-16">{orderData.data.ref}</strong>
                                        </div>
                                        <div className="text-muted small">
                                            <div>Date : {formatDate(orderData.data.date)}</div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Client Information */}
                                {clientInfo && clientInfo.nom && (
                                    <Row className="mb-3 p-3 bg-light rounded">
                                        <Col>
                                            <h6 className="fw-bold mb-2">Client</h6>
                                            <Row>
                                                <Col md={6}>
                                                    <strong>{clientInfo.nom}</strong>
                                                    {clientInfo.name_alias && (
                                                        <div className="text-muted small">
                                                            Alias: {clientInfo.name_alias}
                                                        </div>
                                                    )}
                                                </Col>
                                                <Col md={6}>
                                                    {clientInfo.email && (
                                                        <div className="small">
                                                            <i className="ri-mail-line me-1"></i>
                                                            {clientInfo.email}
                                                        </div>
                                                    )}
                                                    {clientInfo.phone && (
                                                        <div className="small">
                                                            <i className="ri-phone-line me-1"></i>
                                                            {clientInfo.phone}
                                                        </div>
                                                    )}
                                                    {(clientInfo.address || clientInfo.zip || clientInfo.town) && (
                                                        <div className="small">
                                                            <i className="ri-map-pin-line me-1"></i>
                                                            {[clientInfo.address, clientInfo.zip, clientInfo.town].filter(Boolean).join(' ')}
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                )}

                                <div className="table-responsive">
                                    <Table bordered size="sm" className="mb-0">
                                        <thead className="table-light" style={{ fontSize: '0.75rem' }}>
                                            <tr>
                                                <th style={{ width: '30%' }}>Produit</th>
                                                <th className="text-center">Référence</th>
                                                <th className="text-center">Prix Unitaire</th>
                                                <th className="text-center">Qte Commandée</th>
                                                <th className="text-center">Qte à Retourner</th>
                                                <th className="text-center">Motif du Retour</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const filteredLines = orderData.data.lines.filter(line => Number(line.qty) > 0);

                                                if (filteredLines.length === 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan={6} className="text-center py-5">
                                                                <div className="text-muted">
                                                                    <i className="ri-information-line fs-2 d-block mb-2"></i>
                                                                    <strong>Aucun produit de la commande ne peut être retourné</strong>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return filteredLines.map((line) => (
                                                    <tr key={line.rowid}>
                                                        <td style={{ fontSize: '0.8rem' }}>
                                                            <div className="fw-medium">{line.product_label}</div>
                                                            <small className="text-muted">{line.description}</small>
                                                            {line.product_barcode && (
                                                                <div className="small text-muted">
                                                                    Code barre: {line.product_barcode}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="text-center" style={{ fontSize: '0.8rem' }}>
                                                            <Badge color="success">{line.product_ref}</Badge>
                                                        </td>
                                                        <td className="text-center fw-bold text-success" style={{ fontSize: '0.8rem' }}>
                                                            {parseFloat(line.subprice).toFixed(2)} €
                                                        </td>
                                                        <td className="text-center fw-bold" style={{ fontSize: '0.8rem' }}>
                                                            {line.qty}
                                                        </td>
                                                        <td style={{ width: '120px' }}>
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                max={parseInt(line.qty)}
                                                                value={quantites[line.rowid] === 0 ? 0 : quantites[line.rowid]}
                                                                onChange={(e) => handleQuantiteChange(line.rowid, e.target.value)}
                                                                onFocus={(e) => handleInputFocus(line.rowid, e)}
                                                                onBlur={(e) => handleInputBlur(line.rowid, e)}
                                                                disabled={validating || !!pdfUrl}
                                                                className="text-center"
                                                                style={{
                                                                    textAlign: 'center',
                                                                    paddingLeft: '0',
                                                                    paddingRight: '0',
                                                                    fontSize: '0.8rem',
                                                                    padding: '0.25rem'
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={{ width: '200px' }}>
                                                            <Input
                                                                type="text"
                                                                placeholder="Motif du retour..."
                                                                value={descriptions[line.rowid] || ''}
                                                                onChange={(e) => handleDescriptionChange(line.rowid, e.target.value)}
                                                                disabled={validating || !!pdfUrl}
                                                                className="small"
                                                                style={{ fontSize: '0.85rem', padding: '0.25rem' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </Table>
                                </div>

                                {(() => {
                                    const hasLinesWithQty = orderData.data.lines.some(line => Number(line.qty) > 0);
                                    if (!hasLinesWithQty) return null;

                                    return (
                                        <div className="mt-3 p-3 bg-light rounded">
                                            <Row className="text-center align-items-center">
                                                <Col md={4}>
                                                    <div className="text-muted small">Montant total à rembourser</div>
                                                    <div className="h3 text-primary fw-bold mb-0">
                                                        {calculateTotal().toFixed(2)} €
                                                    </div>
                                                    <small className="text-muted">TTC</small>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="text-muted small">Statut</div>
                                                    <Badge color={getStatusBadge(orderData.data.status).color} className="ms-2">
                                                        {getStatusBadge(orderData.data.status).label}
                                                    </Badge>
                                                </Col>
                                                <Col md={4}>
                                                    {orderData && !pdfUrl && (
                                                        <Button
                                                            color="primary"
                                                            onClick={handleValidate}
                                                            disabled={validating}
                                                            className="w-100"
                                                        >
                                                            {validating ? (
                                                                <>
                                                                    <Spinner size="sm" className="me-2" />
                                                                    Validation...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="ri-check-line me-2"></i>
                                                                    Valider le Retour
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>
                                    );
                                })()}
                            </CardBody>
                        </Card>
                    )}
                </ModalBody>
            </Modal>

            {/* Validation Result Modal */}
            <Modal isOpen={showValidationModal} toggle={closeValidationModal} size="lg" centered>
                <ModalHeader toggle={closeValidationModal}>
                    <i className="ri-information-line me-2"></i>
                    Résultat de la Validation
                </ModalHeader>

                <ModalBody>
                    {validationResponse && (
                        <>
                            <Alert
                                color={validationResponse.success ? "success" : "warning"}
                                className="mb-4"
                            >
                                <div className="d-flex align-items-center">
                                    <i className={`ri-${validationResponse.success ? "check" : "error-warning"}-line me-2 fs-5`}></i>
                                    <strong>{validationResponse.message}</strong>
                                </div>
                            </Alert>

                            {validationResponse.resultPDF && (
                                <Card className="mb-4 border-success">
                                    <CardBody className="text-center">
                                        <h6 className="fw-bold mb-3">Document généré avec succès</h6>
                                        <Button
                                            color="danger"
                                            onClick={() => setShowPdfModal(true)}
                                            className="me-2"
                                        >
                                            <i className="ri-file-pdf-line me-1"></i>
                                            Afficher la Bon Commande PDF
                                        </Button>
                                        <Button
                                            color="outline-danger"
                                            onClick={() => window.open(validationResponse.resultPDF, '_blank')}
                                        >
                                            <i className="ri-download-line me-1"></i>
                                            Télécharger
                                        </Button>
                                    </CardBody>
                                </Card>
                            )}

                            {validationLines.length > 0 ? (
                                <>
                                    <h6 className="fw-bold mb-3">Détail des lignes validées:</h6>
                                    <div className="table-responsive">
                                        <Table bordered size="sm">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Produit</th>
                                                    <th className="text-center">Référence</th>
                                                    <th className="text-center">Quantité</th>
                                                    <th className="text-center">Motif</th>
                                                    <th className="text-center">Statut</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {validationLines.map((item, index) => (
                                                    <tr key={item.line.line_id || index}>
                                                        <td>
                                                            <div className="fw-medium">{item.line.product_label}</div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge color="info">{item.line.product_ref}</Badge>
                                                        </td>
                                                        <td className="text-center fw-bold">
                                                            {item.line.qt}
                                                        </td>
                                                        <td className="text-center">
                                                            <small>{item.line.reason}</small>
                                                        </td>
                                                        <td className="text-center">
                                                            {getLineStatusIcon(item.line, item.error)}
                                                            {item.error && (
                                                                <div className="small text-danger mt-1">
                                                                    {item.error}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            ) : (
                                <Alert color="info" className="mb-4">
                                    Aucune donnée de ligne disponible dans la réponse.
                                </Alert>
                            )}

                            {validationResponse.post?.order_ref && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    <div className="text-center">
                                        <strong>Référence Commande: </strong>
                                        {validationResponse.post.order_ref}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={closeValidationModal}>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>

            {/* PDF Modal */}
            <Modal isOpen={showPdfModal} toggle={closePdfModal} size="xl" centered>
                <ModalHeader toggle={closePdfModal}>
                    <i className="ri-file-pdf-line me-2 text-danger"></i>
                    Facture de Retour PDF
                </ModalHeader>
                <ModalBody className="p-0" style={{ height: '70vh' }}>
                    {pdfUrl &&
                        <iframe
                            src={pdfUrl}
                            title="Facture PDF"
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    }
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="danger"
                        onClick={() => {
                            if (pdfUrl) {
                                window.open(pdfUrl, '_blank');
                            }
                        }}
                    >
                        <i className="ri-download-line me-1"></i>
                        Télécharger
                    </Button>
                    <Button color="secondary" onClick={closePdfModal}>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default RetourCommandeModal;